import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ApiResponse, GenericItemApiResponse, GenericListApiResponse } from '../_interface/apiResponse';
import { INotificationToken } from '../_interface/notificationProvider';
import { map, Observable, of, tap } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { AvailableAppVersions, Device, DeviceCommandResult } from '../_interface/device';
import { DeviceConnectivityStatus } from '../_interface/deviceConnectivityStatus';

export enum PushNotificationProvider {
  FCM = "fcm",
  WebPush = "webPush"
}

export enum DeviceApplication {
  User = 0,
  Waiter = 1
}

@Injectable({
  providedIn: 'root'
})
export class DeviceService {

  _deviceID: number = 0;

  _udid: string | null = null;

  constructor(private http: HttpClient, private cookieService: CookieService) { }

  setNotificationChannel(application: DeviceApplication, subscription: INotificationToken) {

    const url = environment.apiBase + `device/SetNotificationChannel`;
    var data = {
      application: application,
      subscription: subscription.token,
      provider: subscription.provider
    }

    return this.http.post<ApiResponse>(url, data).subscribe();
  }

  getAvailableVersions() {
    const url = environment.apiBase + `device/availableVersions`;

    return this.http.get<GenericItemApiResponse<AvailableAppVersions>>(url)
      .pipe(
        map((response: GenericItemApiResponse<AvailableAppVersions>) => {
          if (response.success) {
            return response.item;
          }

          throw new Error(response.errorDescription);
        })
      );
  }

  getDevice(deviceID: any) {
    const url = environment.apiBase + `device/get/${deviceID}`;

    return this.http.get<GenericItemApiResponse<Device>>(url)
      .pipe(
        map((response: GenericItemApiResponse<Device>) => {
          if (response.success) {
            return response.item;
          }

          throw new Error(response.errorDescription);
        })
      );
  }

  companyDevices(companyID: number) {
    const url = environment.apiBase + `device/forCompany/${companyID}`;

    return this.http.get<GenericListApiResponse<Device>>(url)
      .pipe(
        map((response: GenericListApiResponse<Device>) => {
          if (response.success) {
            return response.items;
          }

          throw new Error(response.errorDescription);
        })
      );
  }

  /** Izveštaj povezanosti praćenih uređaja (distributer → sve njegove firme, obična → samo ona). */
  connectivityReport(companyID: number) {
    const url = environment.apiBase + `device/connectivityReport/${companyID}`;

    return this.http.get<GenericListApiResponse<DeviceConnectivityStatus>>(url)
      .pipe(
        map((response: GenericListApiResponse<DeviceConnectivityStatus>) => {
          if (response.success) {
            return response.items;
          }

          throw new Error(response.errorDescription);
        })
      );
  }

  updateDevice(device: Device) {
    const url = environment.apiBase + `device/save`;
    var data = {
      name: device.name,
      deviceID: device.deviceID,
      waiterAppVersion: device.waiterAppVersion,
      kitchenDisplayAppVersion: device.kitchenDisplayAppVersion,
      customerDisplayAppVersion: device.customerDisplayAppVersion,
      adminAppVersion: device.adminAppVersion,
      kioskAppVersion: device.kioskAppVersion,
      userAppVersion: device.userAppVersion,
      connectionSchedule: device.connectionSchedule,
      monitorConnection: device.monitorConnection,
      // Rok snimanja sesija. Kad pozivalac nije admin, device-edit ovo polje uopšte ne postavi,
      // pa ključ ispadne iz JSON-a — što je i namera: server ga upisuje samo za admina, tako da
      // izostanak ne može da obriše tuđe podešavanje.
      clarityUntil: device.clarityUntil
    }

    return this.http.post<ApiResponse>(url, data)
      .pipe(
        map((response: ApiResponse) => {
          if (response.success) {
            return response.success;
          }

          throw new Error(response.errorDescription);
        })
      );
  }

  requestNativeLog(deviceID: number) {
    const url = environment.apiBase + `device/RequestNativeLog/${deviceID}`;

    return this.http.post<ApiResponse>(url, null).subscribe();
  }

  checkVersion(deviceID: number) {
    const url = environment.apiBase + `device/checkVersion/${deviceID}`;

    return this.http.post<ApiResponse>(url, null).subscribe();
  }

  /**
   * Reload i `config` idu samo živoj vezi (bez reda čekanja), pa server vraća da li je uređaj uopšte na
   * vezi. `null` = server koji to još ne vraća (stariji backend) — ishod se ne zna. Hladan observable:
   * komanda se šalje tek na `subscribe`.
   */
  reload(deviceID: number): Observable<DeviceCommandResult | null> {
    const url = environment.apiBase + `device/reload/${deviceID}`;

    return this.http.post<GenericItemApiResponse<DeviceCommandResult> | null>(url, null).pipe(
      map(response => this.commandResult(response))
    );
  }

  sendConfig(deviceID: number, config: any): Observable<DeviceCommandResult | null> {
    const url = environment.apiBase + `device/sendconfig`;
    var data = {
      deviceID: deviceID,
      config: JSON.stringify(config)
    }
    return this.http.post<GenericItemApiResponse<DeviceCommandResult> | null>(url, data).pipe(
      map(response => this.commandResult(response))
    );
  }

  /** Neuspeh (npr. AccessDenied) je već prijavio presretač — u grešku, da pozivalac ne javi i „poslato". */
  private commandResult(response: GenericItemApiResponse<DeviceCommandResult> | null): DeviceCommandResult | null {
    if (response && response.success === false) {
      throw new Error(response.errorDescription || 'Device command failed');
    }

    return response?.item ?? null;
  }

  getID() {
    if (this._deviceID) {
      return of(this._deviceID);
    }
    const url = environment.apiBase + `device/id`;

    return this.http.get<number>(url)
      .pipe(
        tap((id: number) => {
          this._deviceID = id;
        }
        )
      );
  }

  get deviceID(): string {
    if (!this._udid) {
      this._udid = this.cookieService.get("udid");
      if (this._udid) {
        return this._udid;
      }

      this._udid = localStorage.getItem("udid");
      if (!this._udid) {
        this._udid = this.makeid(48);
        localStorage.setItem("udid", this._udid);
      }

      this.cookieService.set("udid", this._udid, 365, "/", this.topLevelDomain);

    }
    return this._udid;
  }

  get topLevelDomain() {
    var parts = location.hostname.split('.');
    return parts.slice(-2).join('.');
  }

  makeid(length: number): string {
    var result = [];
    var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result.push(characters.charAt(Math.floor(Math.random() *
        charactersLength)));
    }
    return result.join('');
  }

  getLogFiles(deviceId: number) {
    const url = `${environment.apiBase}log/${encodeURIComponent(deviceId)}`;
    return this.http.get<string[]>(url);
  }

  downloadLogFile(deviceId: number, fileName: string) {
    const url = `${environment.apiBase}log/${encodeURIComponent(deviceId)}/${encodeURIComponent(fileName)}`;
    return this.http.get(url, { responseType: 'blob' });
  }

}
